// Per-lead actions that are ABOUT the lead rather than the work on it. It lives
// behind a ⋯ on the row itself so it is reachable without expanding, and so the
// expanded area stays about the tasks.
//
// The two terminal stage moves live HERE and nowhere else. They used to be two
// entries in the row's stage dropdown, indistinguishable from nudging a lead from
// "פגישה" to "הצעת מחיר" — which made winning a deal both invisible as an action and
// unconfirmable as a consequence. Named menu items, each opening a dialog that says
// what it does, are the whole point; leaving a second silent path in the dropdown
// would defeat them.
//
// Deliberately not the board's `.row-more` class: that one is hidden on hover
// devices (it only reveals a hover-shown edit button). This menu is the only route
// to these two actions, so it is always visible, on every pointer type.
//
// THE POPOVER IS RENDERED INTO document.body, not next to its button. A lead card is
// `overflow: hidden` — that is what keeps the expanded body inside the card's rounded
// corners — and an absolutely-positioned menu inside it was clipped at the card's
// edge. On a collapsed card there is barely any card below the ⋯, so the menu opened
// effectively invisible: clicked, and no options. No z-index fixes that; a clipped
// descendant is clipped whatever it is stacked above. A portal takes it out of every
// ancestor's overflow at once, which is also why the position has to be computed from
// the button's rect in viewport coordinates rather than left to CSS.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import he from '../../i18n/he';

// Clear of the viewport edge, and of the button, by the same amount the old CSS used.
const EDGE = 8;
const GAP = 4;

export default function LeadMenu({ onEditLead, onConvert, onMarkLost }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null); // { top, left } in viewport coordinates
  const btnRef = useRef(null);
  const popRef = useRef(null);

  // Below the button and aligned to its start edge — where the menu used to sit — then
  // pulled back inside the viewport if there is not room, flipping above the button
  // rather than off the bottom of a long pipeline.
  const place = useCallback(() => {
    const btn = btnRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const pop = popRef.current;
    const w = pop ? pop.offsetWidth : 0;
    const h = pop ? pop.offsetHeight : 0;
    let top = r.bottom + GAP;
    if (h && top + h > window.innerHeight - EDGE) top = Math.max(EDGE, r.top - h - GAP);
    let left = r.left;
    if (w && left + w > window.innerWidth - EDGE) left = window.innerWidth - w - EDGE;
    setPos({ top, left: Math.max(EDGE, left) });
  }, []);

  // First pass runs before paint with the popover measured, so it never appears at a
  // stale position and jumps.
  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  // Close on an outside click or Escape — a popover that can only be dismissed by
  // re-tapping its own trigger is a trap on a phone. The popover is no longer a DOM
  // descendant of this component, so "outside" has to name both nodes.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (btnRef.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    // A fixed popover does not travel with the page, so it is re-placed on any scroll
    // (capture: true — the scroll may be in a container, not the window) and on resize.
    const onMove = () => place();
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, place]);

  const pick = (fn) => () => { setOpen(false); fn(); };

  return (
    <div className="lead-menu">
      <button
        type="button"
        ref={btnRef}
        className="lead-more"
        aria-label={he.common.actions}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        ⋯
      </button>
      {open && createPortal(
        <div
          className="lead-menu-pop"
          role="menu"
          ref={popRef}
          // Hidden for the first frame only — until it has been measured there is no
          // honest position to draw it at.
          style={pos ? { top: pos.top, left: pos.left } : { visibility: 'hidden' }}
        >
          <button type="button" role="menuitem" onClick={pick(onEditLead)}>{he.leads.editLead}</button>
          <button type="button" role="menuitem" onClick={pick(onConvert)}>{he.leads.convert}</button>
          {/* Separated and tinted: losing a deal is the one entry here with no way back. */}
          <div className="menu-sep" role="separator" />
          <button type="button" role="menuitem" className="is-danger" onClick={pick(onMarkLost)}>
            {he.leads.markLost}
          </button>
        </div>,
        document.body,
      )}
    </div>
  );
}
